// rfq-modal.js
//
// RFQ is a revenue path, not a form. Website RFQs become real Zoho quotes worth five
// figures each, so the rules here are:
//   1. This file must never throw on load. Every top-level lookup is guarded.
//   2. A submission must never fail silently. If it does not land, the customer is told
//      and offered a retry and a phone number.
//   3. Nothing here may discard what the customer typed without saying so.
//   4. One click must never become two records.
//
// ENDPOINT (2026-08-09). RFQ_ENDPOINT now points at an Apps Script owned by the business.
// The previous endpoint authenticated to Zoho with a refresh token issued to an individual
// user account, so every website RFQ arrived attributed to that user - and would have
// stopped arriving silently, with no error and no alert, the moment that account was
// deactivated or the token revoked. The old URL is kept commented directly below as the
// rollback: swap the two lines back and republish.
//
// The new endpoint also adds an idempotency key. A repeat submission of the same cart by
// the same person inside 10 minutes returns the existing record instead of creating a
// second one, and email and phone are normalised before matching. Together with the
// timeout change below that closes both duplicate paths.
//
// NOTE ON TRANSPORT: submission still uses JSONP against a doGet endpoint. That is
// deliberate - the contract is unchanged from the previous script, so this swap is a
// credential change and nothing else. Moving to a POST, or to a Shopify draft order, is a
// separate piece of work.
//
// DUPLICATE HISTORY (2026-08-07). A sweep of all 74 cart-pipe RFQs found 8 duplicate
// incidents - 11% - worth $93,000 of duplicated order value. TWO separate causes:
//
//   (a) TIMEOUT RETRIES. RFQ_TIMEOUT_MS was 20000. The endpoint writes to Zoho BEFORE
//       returning the JSONP callback, and a cold start alone can run 10-30 seconds - so the
//       20s timer regularly fired on submissions that had ALREADY SUCCEEDED. The customer
//       was told it failed, clicked Try again, and a duplicate was created. On 4 Aug one
//       customer sent the same $786 order four times: 16:59:20, 16:59:44, 17:00:43,
//       17:01:16 - every gap longer than 20s. Timeout raised to 60000 and the failure copy
//       no longer asserts a failure it cannot verify.
//
//   (b) RAPID DOUBLE SUBMITS, 1-3 seconds apart. submitRfq() clears the button container
//       straight away, so a plain second click should not land - the likeliest path is two
//       click events queued before the first handler finishes. That is inferred, not
//       proven, so the guard below is deliberately explicit rather than relying on the
//       container clear.
//
// The real cost of a duplicate is not clutter. Two pairs ended up with CONTRADICTORY
// STATUSES on the same order - one $36,648 quote was 'Closed Lost' on one record and
// 'Ready to Quote' on the other - because two people worked two copies without knowing the
// other existed.

var RFQ_ENDPOINT = "https://script.google.com/macros/s/AKfycbydwk5HGwDymcOt2Z8iRWGotlUWWUK41ajKnVTq2mV1fA74YXoZzkzi7BpaNL6OH8nE/exec";
// ROLLBACK - previous endpoint, restore this line and republish if the new pipe misbehaves:
// var RFQ_ENDPOINT = "https://script.google.com/macros/s/AKfycbx0tyw2AZv7zIAL-jCjiMBztqhtRYvhNC7M4cT-SPxBeW5q-40tY8Q_2FogYlvms0Qi/exec";

var FEEDBACK_ENDPOINT = "https://script.google.com/macros/s/AKfycbzDiXtCNkGzNdnPJ-sCWyqvsgjkZVocsPVsKwXlgeu1-lloJCgVhgriYfLGYp9UzYLD/exec";
var RFQ_TIMEOUT_MS = 60000;
var RFQ_PHONE = "1-844-663-8257";

// Set true the moment a valid submission starts, so a second click cannot start another.
// Reset only if the submission comes back unconfirmed and the customer chooses to resend.
var rfqSubmitInFlight = false;

// Required fields, with the labels shown to the customer when one is missing.
var RFQ_REQUIRED = [
  { id: 'rfq-first-name', label: 'First name' },
  { id: 'rfq-last-name',  label: 'Last name' },
  { id: 'rfq-email',      label: 'Email' },
  { id: 'rfq-phone',      label: 'Phone' },
  { id: 'rfq-zip',        label: 'Zip' }
];

// GUARDED: previously this was an unguarded getElementById(...).addEventListener at the top
// of the file. On any page without the trigger button it threw a TypeError and every
// function below it never registered - no modal, no submission, no error shown.
(function bindRfqTrigger() {
  var trigger = document.getElementById('openRfqModalBtn');
  if (!trigger) return;
  trigger.addEventListener('click', openRfqModal);
})();

function openRfqModal() {
  var modal = document.getElementById('rfq-modal');
  if (!modal) return;
  rfqSubmitInFlight = false;
  modal.classList.remove('hidden');
  renderRfqModal('step1');
}

// Define the modal flow.
var rfqModalFlows = {
  step1: {
    header: "Request Your Quote",
    content:
      "<p>Please confirm your information:</p>" +
      "<div style='display: flex; flex-direction: column; gap: 1rem; margin-top: 1rem; margin-bottom: 1rem;'>" +
        "<div style='display: flex; gap: 1rem;'>" +
          "<input type='text' id='rfq-first-name' placeholder='First Name' style='flex:1;' />" +
          "<input type='text' id='rfq-last-name' placeholder='Last Name' style='flex:1;' />" +
        "</div>" +
        "<div style='display: flex; gap: 1rem;'>" +
          "<input type='tel' id='rfq-phone' placeholder='Phone' style='flex:1;' />" +
          "<input type='text' id='rfq-zip' placeholder='Zip' style='flex:1;' />" +
        "</div>" +
        "<div style='display: flex; gap: 1rem;'>" +
          "<input type='email' id='rfq-email' placeholder='Email' style='flex:1; max-width:100%; box-sizing:border-box;' />" +
        "</div>" +
        "<input type='text' id='rfq-company' placeholder='Company' />" +
        "<input type='text' id='rfq-address' placeholder='Address' />" +
        "<div style='display: flex; gap: 1rem;'>" +
          "<input type='text' id='rfq-city' placeholder='City' style='flex:1;' />" +
          "<input type='text' id='rfq-state' placeholder='State' style='flex:1;' />" +
        "</div>" +
      "</div>" +
      "<div id='rfq-form-error' role='alert' style='color:#A32D2D; font-size:0.875rem; margin-bottom:1rem; min-height:1.1rem;'></div>" +
      "<div id='rfq-cart-table'></div>" +
      "<p style='margin-top: 2rem; margin-bottom: 1rem; font-style: italic;'>Note: Quotes are based on your shipping zip code. Please make sure that you have entered it correctly.</p>",
    buttons: [
      // Was labelled "Back", which reads as "previous step" but actually closed the modal
      // and discarded everything typed. There is no step before this one.
      { text: "Cancel", action: "close" },
      { text: "Submit", action: "step2" }
    ]
  },
  step2: {
    header: "Got a Second?",
    image: "url(https://cdn.shopify.com/s/files/1/0600/3300/7699/files/5423350_2808354.svg?v=1742482732)",
    content:
      "<p style='margin-bottom: 2rem;'>We'd love to know how you're enjoying Metals Delivered!</p>" +
      "<label for='feedback-dropdown'>Feedback Type:</label><br/>" +
      "<select id='feedback-dropdown' style='margin-top: .5rem; margin-bottom: 1rem;'>" +
        "<option value=''>Select type</option>" +
        "<option value='general'>General</option>" +
        "<option value='product'>Product</option>" +
        "<option value='service'>Service</option>" +
        "<option value='other'>Other</option>" +
      "</select><br/>" +
      "<label for='feedback-textarea'>Your Feedback:</label><br/>" +
      "<textarea id='feedback-textarea' rows='4' style='margin-top: .5rem; width:100%;'></textarea>",
    buttons: [
      { text: "Skip", action: "step4_logged_in", condition: "loggedIn" },
      { text: "Leave Feedback", action: "step3_logged_in", condition: "loggedIn", id: "leave-feedback-btn" },
      { text: "Skip", action: "step3_guest", condition: "guest" },
      { text: "Send Feedback", action: "step3_guest", condition: "guest", id: "leave-feedback-btn" }
    ]
  },
  // The image: entries below previously pointed at /assets/*.png files that do not exist in
  // this theme, so every one of these screens 404'd its illustration. Removed rather than
  // guessed at. Add real ones with {{ 'name.png' | asset_url }} if wanted.
  step3_logged_in: {
    header: "Thank You!",
    content: "We appreciate your time. We'll be in touch soon!",
    buttons: [ { text: "OK", action: "close" } ]
  },
  step4_logged_in: {
    header: "All Set!",
    content: "Thanks again for your request!",
    buttons: [ { text: "Close", action: "close" } ]
  },
  step3_guest: {
    header: "Create Account",
    content: "Your request has been submitted! Want to create an account?",
    buttons: [
      { text: "Skip", action: "final_step1_guest" },
      { text: "Create Account", link: "/account/register" }
    ]
  },
  final_step1_guest: {
    header: "Thank You!",
    content: "Thanks again! We'll get back to you regarding your quote ASAP!",
    buttons: [ { text: "OK", action: "close" } ]
  },
  final_step2_guest: {
    header: "Nice Job!",
    content: "We've submitted your request! Would you like to explore on-boarding?",
    buttons: [
      { text: "No Thanks", link: "/" },
      { text: "Continue To Onboarding", link: "/collections/all" }
    ]
  }
};

// --- small DOM helpers, all null-safe ---
function rfqSetHeader(text) {
  var el = document.getElementById('rfq-modal-header');
  if (el) el.innerText = text;
}

function rfqSetImage(image) {
  var el = document.getElementById('rfq-modal-image');
  if (!el) return;
  // Previously assigned modalData.image unconditionally, so steps without an image got the
  // literal string "undefined" as a background-image.
  el.style.backgroundImage = image || 'none';
  el.style.display = image ? '' : 'none';
}

function rfqSetText(html) {
  var el = document.getElementById('rfq-modal-text');
  if (el) el.innerHTML = html;
}

function rfqButtonsContainer() {
  return document.getElementById('rfq-modal-buttons');
}

function rfqValue(id) {
  var el = document.getElementById(id);
  return el && el.value ? el.value.trim() : "";
}

// Render the modal content for the current step.
function renderRfqModal(step) {
  var modalData = rfqModalFlows[step];
  if (!modalData) return;

  rfqSetHeader(modalData.header);
  rfqSetImage(modalData.image);
  rfqSetText(modalData.content);

  var buttonsContainer = rfqButtonsContainer();
  if (!buttonsContainer) return;
  buttonsContainer.innerHTML = '';

  var visibleButtons = modalData.buttons.filter(function(button) {
    if (button.condition && button.condition !== (window.customerIsLoggedIn ? 'loggedIn' : 'guest')) {
      return false;
    }
    return true;
  });

  visibleButtons.forEach(function(button, index) {
    var btn = document.createElement('button');
    btn.innerText = button.text;

    if (step === 'step1') {
      btn.className = (index === 0) ? "btn1 btn1-ghost" : "btn1 btn1-primary";
    } else if (visibleButtons.length === 1) {
      btn.className = "btn1 btn1-primary";
    } else {
      btn.className = (index === 0) ? "btn1 btn1-default" : "btn1 btn1-primary";
    }

    if (button.id) btn.id = button.id;

    if (step === 'step1' && button.text.toLowerCase() === 'submit') {
      // Stable id so validation does not depend on nth-child position.
      btn.id = 'rfq-submit-btn';
      btn.addEventListener('click', function(event) {
        event.preventDefault();

        // DOUBLE-SUBMIT GUARD. Two checks, deliberately belt-and-braces: a module-level flag
        // that survives re-render, and the button's own disabled state. A click event already
        // queued before the first handler ran will still fire on the detached element, and
        // the flag catches that.
        if (rfqSubmitInFlight || btn.disabled) return;

        var missing = rfqMissingFields();
        if (missing.length) {
          // Previously the button was made unclickable with pointer-events:none and a
          // generic alert, so the customer had no idea which field was the problem.
          // A validation failure must NOT latch the guard - they need to fix it and retry.
          rfqShowFieldError("Please add: " + missing.join(", "));
          return;
        }

        rfqShowFieldError("");
        rfqSubmitInFlight = true;
        btn.disabled = true;
        btn.style.opacity = '0.6';
        btn.innerText = 'Sending...';
        submitRfq();
      });
    } else if (step === 'step2' && button.id === 'leave-feedback-btn') {
      btn.addEventListener('click', function(event) {
        event.preventDefault();
        submitFeedback(button.action);
      });
    } else {
      btn.addEventListener('click', function() {
        if (button.link) {
          window.location.href = button.link;
        } else {
          handleRfqModalAction(button.action);
        }
      });
    }

    buttonsContainer.appendChild(btn);
  });

  if (step === 'step1') {
    autofillRfqInputs();
    populateCartTable();
    setupStep1Validation();
  }
  if (step === 'step2') {
    setupFeedbackValidation();
  }
}

function handleRfqModalAction(action) {
  if (action === 'close') {
    var modal = document.getElementById('rfq-modal');
    if (modal) modal.classList.add('hidden');
    rfqSubmitInFlight = false;
  } else if (typeof action === 'string' && action.indexOf('http') === 0) {
    window.location.href = action;
  } else {
    renderRfqModal(action);
  }
}

function autofillRfqInputs() {
  var sourceFields = {
    company:   document.getElementById('shipto-company'),
    firstName: document.getElementById('shipto-first_name'),
    lastName:  document.getElementById('shipto-last_name'),
    email:     { value: window.customerEmail },
    phone:     document.getElementById('shipto-phone'),
    address:   document.getElementById('shipto-address1'),
    city:      document.getElementById('shipto-city'),
    state:     document.getElementById('shipto-province'),
    zip:       document.getElementById('shipto-zip')
  };

  var modalFields = {
    company:   document.getElementById('rfq-company'),
    firstName: document.getElementById('rfq-first-name'),
    lastName:  document.getElementById('rfq-last-name'),
    email:     document.getElementById('rfq-email'),
    phone:     document.getElementById('rfq-phone'),
    address:   document.getElementById('rfq-address'),
    city:      document.getElementById('rfq-city'),
    state:     document.getElementById('rfq-state'),
    zip:       document.getElementById('rfq-zip')
  };

  Object.keys(sourceFields).forEach(function(key) {
    if (sourceFields[key] && modalFields[key]) {
      modalFields[key].value = sourceFields[key].value || '';
    }
  });
}

function populateCartTable() {
  fetch('/cart.js')
    .then(function(response) { return response.json(); })
    .then(function(cart) {
      var tableContainer = document.getElementById('rfq-cart-table');
      if (!tableContainer) return;

      var items = (cart && cart.items) ? cart.items : [];
      if (items.length === 0) {
        tableContainer.innerHTML = "<p>Your cart is empty.</p>";
        return;
      }

      var tableHtml = "<table style=\"width:100%; border-collapse: collapse; margin-top: 1rem;\">" +
        "<thead><tr>" +
          "<th style=\"text-align: left; border: 1px solid #ddd; padding: 8px;\">Product</th>" +
          "<th style=\"text-align: left; border: 1px solid #ddd; padding: 8px;\">Qty</th>" +
          "<th style=\"text-align: left; border: 1px solid #ddd; padding: 8px;\">Price</th>" +
        "</tr></thead><tbody>";

      items.forEach(function(item) {
        tableHtml += "<tr>" +
          "<td style=\"border: 1px solid #ddd; padding: 8px;\">" + item.product_title + "</td>" +
          "<td style=\"border: 1px solid #ddd; padding: 8px;\">" + item.quantity + "</td>" +
          "<td style=\"border: 1px solid #ddd; padding: 8px;\">" + formatMoney(item.line_price) + "</td>" +
        "</tr>";
      });

      tableHtml += "</tbody></table>";
      tableContainer.innerHTML = tableHtml;
    })
    .catch(function(error) {
      console.error('[RFQ] Error fetching cart for table:', error);
      var tableContainer = document.getElementById('rfq-cart-table');
      if (tableContainer) tableContainer.innerHTML = "<p>We could not load your cart just now. You can still send this request.</p>";
    });
}

// --- validation ---
function rfqMissingFields() {
  return RFQ_REQUIRED
    .filter(function(f) { return rfqValue(f.id) === ""; })
    .map(function(f) { return f.label; });
}

function rfqShowFieldError(message) {
  var el = document.getElementById('rfq-form-error');
  if (el) el.textContent = message || "";
}

function setupStep1Validation() {
  setTimeout(function() {
    var btn = document.getElementById('rfq-submit-btn');
    if (!btn) return;

    function refresh() {
      // Never re-enable or re-style a button that has already been committed to a send.
      if (rfqSubmitInFlight || btn.disabled) return;
      var missing = rfqMissingFields();
      // Dim to signal incompleteness, but keep the button clickable so the customer can
      // find out WHY it is not ready.
      btn.style.opacity = missing.length ? '0.6' : '1';
      if (!missing.length) rfqShowFieldError("");
    }

    RFQ_REQUIRED.forEach(function(f) {
      var el = document.getElementById(f.id);
      if (el) el.addEventListener('input', refresh);
    });

    refresh();
  }, 100);
}

// --- submission ---
function rfqShowSending() {
  rfqSetHeader("Sending your request");
  rfqSetImage(null);
  rfqSetText("<p>One moment - we are sending your request to our team. This can take up to a minute, so please do not close this window.</p>");
  var container = rfqButtonsContainer();
  if (container) container.innerHTML = '';
}

// The whole point of this function: the customer finds out. Previously a failed JSONP call
// left the modal frozen on step 1 with no message, and the lead existed nowhere.
//
// This screen used to state flatly that the request "did not reach us" and put a Try again
// button front and centre. Because the old 20s timeout fired on submissions that had
// already succeeded, that wording was often untrue and the retry created duplicate RFQs.
// We can only honestly say we have not had confirmation yet.
function rfqShowFailure(formData, cartData) {
  rfqSetHeader("We have not had confirmation yet");
  rfqSetImage(null);
  rfqSetText(
    "<p>We have not had confirmation back yet. Your request may still have reached us - our system can take a minute to answer.</p>" +
    "<p><strong>Please only send it again if you are sure it did not go through</strong>, otherwise we may end up with duplicates of your order. If you would rather be certain, call us and we will take the details straight from you:</p>" +
    "<p style='font-size:1.15rem;'><strong>" + RFQ_PHONE + "</strong></p>"
  );

  var container = rfqButtonsContainer();
  if (!container) return;
  container.innerHTML = '';

  var closeBtn = document.createElement('button');
  closeBtn.className = 'btn1 btn1-primary';
  closeBtn.innerText = 'Close';
  closeBtn.addEventListener('click', function() { handleRfqModalAction('close'); });

  var retryBtn = document.createElement('button');
  retryBtn.className = 'btn1 btn1-ghost';
  retryBtn.innerText = 'Send it again';
  retryBtn.addEventListener('click', function() {
    // A deliberate resend by the customer is the one place the guard is released, and it is
    // re-latched immediately so the resend itself cannot be double-fired. The endpoint also
    // deduplicates on its side, so a resend inside 10 minutes is now harmless.
    if (retryBtn.disabled) return;
    retryBtn.disabled = true;
    rfqSubmitInFlight = true;
    rfqShowSending();
    sendRfqJsonp(formData, cartData);
  });

  container.appendChild(closeBtn);
  container.appendChild(retryBtn);
}

function submitRfq() {
  var formData = {
    company:   rfqValue('rfq-company'),
    firstName: rfqValue('rfq-first-name'),
    lastName:  rfqValue('rfq-last-name'),
    email:     rfqValue('rfq-email'),
    phone:     rfqValue('rfq-phone'),
    address:   rfqValue('rfq-address'),
    city:      rfqValue('rfq-city'),
    state:     rfqValue('rfq-state'),
    zip:       rfqValue('rfq-zip')
  };

  rfqShowSending();

  fetch('/cart.js')
    .then(function(response) { return response.json(); })
    .then(function(cart) {
      var items = (cart && cart.items) ? cart.items : [];
      var cartData = items.map(function(item) {
        return {
          productTitle: item.product_title,
          variantTitle: item.variant_title,
          sku:          item.sku,
          variantId:    item.variant_id,
          quantity:     item.quantity,
          priceEach:    formatMoney(item.price),
          totalPrice:   formatMoney(item.line_price)
        };
      });
      sendRfqJsonp(formData, cartData);
    })
    .catch(function(error) {
      // A cart read failure used to abort the whole submission and lose the lead. Send the
      // contact details anyway - a named prospect with no line items still beats nothing.
      console.error('[RFQ] Could not read cart, sending contact details only:', error);
      sendRfqJsonp(formData, []);
    });
}

function sendRfqJsonp(formData, cartData) {
  var callbackName = "rfqCallback_" + Date.now();
  var settled = false;
  var scriptEl = null;
  var timer = null;

  function cleanup() {
    if (timer) clearTimeout(timer);
    if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    try { delete window[callbackName]; } catch (e) { window[callbackName] = undefined; }
  }

  function fail(reason) {
    if (settled) return;
    settled = true;
    console.error('[RFQ] No confirmation:', reason, formData);
    cleanup();
    rfqShowFailure(formData, cartData);
  }

  window[callbackName] = function(response) {
    if (settled) return;
    settled = true;
    console.log('[RFQ] Submission acknowledged:', response);
    cleanup();
    handleRfqModalAction('step2');
  };

  var params = new URLSearchParams({
    callback:  callbackName,
    type:      'rfq',
    company:   formData.company,
    firstName: formData.firstName,
    lastName:  formData.lastName,
    email:     formData.email,
    phone:     formData.phone,
    address:   formData.address,
    city:      formData.city,
    state:     formData.state,
    zip:       formData.zip,
    cartData:  JSON.stringify(cartData)
  });

  scriptEl = document.createElement('script');
  scriptEl.src = RFQ_ENDPOINT + "?" + params.toString();
  scriptEl.onerror = function() { fail('script load error'); };

  timer = setTimeout(function() { fail('timeout after ' + RFQ_TIMEOUT_MS + 'ms'); }, RFQ_TIMEOUT_MS);

  document.body.appendChild(scriptEl);
}

// --- feedback (optional, so failure just moves the customer along) ---
function submitFeedback(actionAfter) {
  var feedbackDropdown = document.getElementById('feedback-dropdown');
  var feedbackTextarea = document.getElementById('feedback-textarea');

  var feedbackData = {
    feedbackType: feedbackDropdown ? feedbackDropdown.value : '',
    feedbackText: feedbackTextarea ? feedbackTextarea.value.trim() : ''
  };

  var callbackName = "feedbackCb_" + Date.now();
  var settled = false;
  var scriptEl = null;
  var timer = null;

  function cleanup() {
    if (timer) clearTimeout(timer);
    if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    try { delete window[callbackName]; } catch (e) { window[callbackName] = undefined; }
  }

  function settle(logLabel, detail) {
    if (settled) return;
    settled = true;
    console.log('[RFQ feedback] ' + logLabel, detail);
    cleanup();
    // Feedback is a nice-to-have. Never trap the customer here, even on failure.
    handleRfqModalAction(actionAfter);
  }

  window[callbackName] = function(response) { settle('acknowledged:', response); };

  var params = new URLSearchParams({
    callback:     callbackName,
    type:         'feedback',
    feedbackType: feedbackData.feedbackType,
    feedbackText: feedbackData.feedbackText
  });

  scriptEl = document.createElement('script');
  scriptEl.src = FEEDBACK_ENDPOINT + "?" + params.toString();
  scriptEl.onerror = function() { settle('send failed, continuing anyway', feedbackData); };

  timer = setTimeout(function() { settle('timed out, continuing anyway', feedbackData); }, RFQ_TIMEOUT_MS);

  document.body.appendChild(scriptEl);
}

function setupFeedbackValidation() {
  var dropdown = document.getElementById('feedback-dropdown');
  var textarea = document.getElementById('feedback-textarea');
  var feedbackBtn = document.getElementById('leave-feedback-btn');

  // Previously these addEventListener calls were unguarded and threw if the elements were
  // absent.
  if (!dropdown || !textarea) return;

  if (feedbackBtn) feedbackBtn.disabled = true;

  function checkValidation() {
    if (!feedbackBtn) return;
    feedbackBtn.disabled = !(dropdown.value !== "" && textarea.value.trim() !== "");
  }

  dropdown.addEventListener('change', checkValidation);
  textarea.addEventListener('input', checkValidation);
}

// Utility to format cents to dollars. Defined once - there were three identical copies.
function formatMoney(cents) {
  var n = Number(cents);
  if (!isFinite(n)) return '$0.00';
  return '$' + (n / 100).toFixed(2);
}
